/**
 * `SheetContent` — a dialog on a desktop, a bottom sheet on a phone (mobile M3).
 *
 * Every dialog in MyDeck is a Radix dialog. On a phone a centred box is the
 * wrong shape: it floats out of thumb reach and its close button sits in a top
 * corner. Below `md` this renders the same dialog as a sheet that slides up
 * from the bottom edge, carries a grab handle, and closes when dragged down.
 * At `md` and up every class here is inert, so desktop dialogs are pixel for
 * pixel what they were — the phone classes are all `max-md:` variants.
 *
 * Drop-in for `Dialog.Content`: pass the desktop classes as `className`.
 */

import * as Dialog from "@radix-ui/react-dialog";
import * as React from "react";

import { cn } from "@/lib/cn";

/** Below md: pinned to the bottom edge, full width, rounded on top only, and
 *  clear of the gesture bar. */
const PHONE = cn(
  "sheet-phone",
  "max-md:inset-x-0 max-md:bottom-0 max-md:left-0 max-md:top-auto",
  "max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0",
  "max-md:max-h-[92dvh] max-md:rounded-b-none",
  "max-md:pb-[var(--inset-bottom)]",
);

/** How far down (px) a drag must travel to close the sheet. */
const CLOSE_AFTER = 90;

export const SheetContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof Dialog.Content>
>(({ className, children, ...props }, forwarded) => {
  const local = React.useRef<HTMLDivElement | null>(null);
  const closer = React.useRef<HTMLButtonElement>(null);
  const drag = React.useRef<{ y: number; dy: number } | null>(null);

  const setRef = (node: HTMLDivElement | null) => {
    local.current = node;
    if (typeof forwarded === "function") forwarded(node);
    else if (forwarded) forwarded.current = node;
  };

  const move = (dy: number) => {
    const el = local.current;
    if (!el) return;
    // `translate`, not `transform`: Tailwind's positioning classes own
    // `transform`, and the two compose rather than fight.
    el.style.translate = dy > 0 ? `0 ${dy}px` : "";
  };

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { y: e.clientY, dy: 0 };
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (local.current) local.current.style.transition = "none";
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    move(drag.current.dy);
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    const el = local.current;
    if (!d || !el) return;
    el.style.transition = "translate 0.25s cubic-bezier(0.16, 1, 0.3, 1)";
    if (d.dy > CLOSE_AFTER) closer.current?.click();
    else move(0);
  };

  return (
    <Dialog.Content ref={setRef} className={cn("dialog-content", className, PHONE)} {...props}>
      {/* The grab handle — phones only. Drag it down to close. */}
      <div
        aria-hidden
        className="sticky top-0 z-10 -mb-2 flex h-6 touch-none cursor-grab items-center justify-center md:hidden"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="h-1 w-10 rounded-full bg-hairline-strong" />
      </div>
      <Dialog.Close ref={closer} className="hidden" tabIndex={-1} aria-hidden />
      {children}
    </Dialog.Content>
  );
});
SheetContent.displayName = "SheetContent";
